import type { WireResearchUse } from '@/api/hooks';
import type { ItemContext } from '@/ui/context';
import { productName } from '@/prospecting/words';
import type { ParsedResearch } from './ResearchView';

/**
 * What to do with a research answer next, and what each of its uses is
 * (ADR-0175). Pure: built from the answer the page already parsed and the
 * uses it already holds, so a step never says something the answer does not.
 *
 * Each step is a task waiting to be made, for whoever the person picks: them,
 * a colleague, or one of the department's agents. Nothing here makes it;
 * the person's press on "Make it a task" does, through the usual task door.
 */

export interface NextStepSuggestion {
  key: string;
  /** What the step is, as a button's heading. */
  label: string;
  /** Why it is worth doing, from the answer. */
  why: string;
  /** The task it becomes. */
  title: string;
  body: string;
  /** Where the work is done, when the platform has a page for it. */
  to?: { path: string; label: string } | undefined;
}

const list = (xs: readonly string[]): string => (xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`);

export function nextSteps(parsed: ParsedResearch, requestId: string): NextStepSuggestion[] {
  const from = `From the research on /requests/${requestId}.`;
  if (parsed.kind === 'market_answer') {
    const a = parsed.answer;
    const code = a.marketCode;
    const sourced = a.findings.filter((f) => f.provenance === 'sourced');
    const unread = sourced.filter((f) => (f.checked?.length ?? 0) === 0);
    const missing = a.findings.filter((f) => f.provenance === 'not_found');
    const steps: NextStepSuggestion[] = [];
    if (unread.length > 0) {
      steps.push({
        key: 'read-back',
        label: `Read back ${unread.length} finding${unread.length === 1 ? '' : 's'} nobody has checked`,
        why: `${list(unread.map((f) => f.criterion))} rest${unread.length === 1 ? 's' : ''} on pages the platform has not read back. Rely on them only once a page is seen to hold them.`,
        title: `Read back the ${code} findings on ${unread.map((f) => f.criterion).join(', ')}`.slice(0, 200),
        body: [
          `Open each page and check it says what the finding says. Record it as sourced on the market's page if it does; correct it if not.`,
          ...unread.map((f) => `- ${f.criterion}: ${f.finding}\n  Pages: ${f.sources.map((s) => s.url).join(' ')}`),
          from,
        ].join('\n\n'),
        to: { path: `/markets/${code}`, label: `Open ${code}` },
      });
    }
    if (missing.length > 0) {
      steps.push({
        key: 'fill-gaps',
        label: `Find sources for ${missing.length} criteri${missing.length === 1 ? 'on' : 'a'} not found`,
        why: `Nothing was found on ${list(missing.map((f) => f.criterion))}. A go or not-now decision is weaker without ${missing.length === 1 ? 'it' : 'them'}.`,
        title: `Find sources for ${code}: ${missing.map((f) => f.criterion).join(', ')}`.slice(0, 200),
        body: [`The research found nothing on these. Look for a trade body, a statistics office or a published report for each, and add what is found with its page.`, ...missing.map((f) => `- ${f.criterion}`), from].join('\n\n'),
        to: { path: `/markets/${code}`, label: `Open ${code}` },
      });
    }
    steps.push({
      key: 'decide',
      label: `Decide whether ${code} is a go`,
      why: `${sourced.length} of ${a.findings.length} criteria are answered from sources. The market's page holds them beside its prices and freight.`,
      title: `Decide go or not now for ${code}${a.product ? ` (${productName(a.product)})` : ''}`,
      body: [`Read the findings on the market's page and decide: go, not now, or more research. Say why in a sentence; the decision is kept with the market.`, from].join('\n\n'),
      to: { path: `/markets/${code}`, label: `Open ${code}` },
    });
    steps.push({
      key: 'prospects',
      label: `Find buyers in ${code}`,
      why: 'Distributors, wholesalers and chains are found through a prospecting plan the superadmin approves, city by city.',
      title: `Set up prospecting in ${code}`,
      body: [`Set up a profile and a territory for ${code} and propose a plan for the superadmin to approve. "Find the uses" on the research proposes the businesses it names for the plan to find.`, from].join('\n\n'),
      to: { path: '/prospecting/setup', label: 'Set up prospecting' },
    });
    return steps;
  }
  if (parsed.kind === 'distributor_answer') {
    const a = parsed.answer;
    const code = a.marketCode;
    const names = a.candidates.map((c) => `- ${c.name}: ${c.website}`).join('\n');
    if (a.candidates.length === 0) {
      return [
        {
          key: 'search-again',
          label: 'Find distributors another way',
          why: 'No distributor was found with a site that could be read.',
          title: `Find distributors in ${code} another way`,
          body: [`The search found no distributor whose own site could be read. Try the national food distributors' association, trade fairs, or the suppliers named on restaurant menus, and add each with its site.`, from].join('\n\n'),
          to: { path: '/prospecting/setup', label: 'Set up prospecting' },
        },
      ];
    }
    return [
      {
        key: 'take',
        label: `Take the distributors worth pursuing`,
        why: `${a.candidates.length} found, each with what its own site says. Taken, they are found again under the ${code} plan and become prospects.`,
        title: `Choose which ${code} distributors to pursue`,
        body: [`Read each distributor below and on its site. The lead takes the ones that carry specialty foods and supply restaurants; the rest are declined with the reason.`, names, from].join('\n\n'),
        to: { path: '/prospecting/plans', label: 'Plans' },
      },
      {
        key: 'approach',
        label: 'Prepare the first approach',
        why: 'A distributor reads a short note with the product, the price band and a sample offer.',
        title: `Prepare the first note to the ${code} distributors`,
        body: [`Draft a short note for each distributor taken: what we make, why it suits their range, and a sample. Nothing is sent by the platform; each note is a draft for a person.`, names, from].join('\n\n'),
      },
    ];
  }
  if (parsed.kind === 'supplier_answer') {
    const a = parsed.answer;
    const code = a.marketCode;
    const makers = a.candidates;
    if (makers.length === 0) {
      return [
        {
          key: 'search-again',
          label: 'Search again, more widely',
          why: 'No maker was found with a site that could be read.',
          title: `Find manufacturers in ${code} another way`,
          body: [`The search found no maker whose own site could be read. Try trade fairs, the national food producers' association, or a supplier directory, and add each maker with its site on Suppliers.`, from].join('\n\n'),
          to: { path: '/suppliers', label: 'Open Suppliers' },
        },
      ];
    }
    const names = makers.map((m) => `- ${m.name}: ${m.website}`).join('\n');
    const priced = makers.filter((m) => m.points.some((p) => p.key === 'price'));
    return [
      {
        key: 'shortlist',
        label: `Read and shortlist ${makers.length} maker${makers.length === 1 ? '' : 's'}`,
        why: 'Each is a candidate on Suppliers with what its own pages say. A main supplier and a backup are chosen from at least three compared.',
        title: `Shortlist manufacturers in ${code}`,
        body: [`Read each candidate on Suppliers and its pages. Shortlist the ones worth writing to; reject the rest with a reason.`, names, from].join('\n\n'),
        to: { path: '/suppliers', label: 'Open Suppliers' },
      },
      {
        key: 'prices',
        label: 'Ask each for a price list, in writing',
        why: `${priced.length} of ${makers.length} named a price on a page. A price counts once the maker writes it (ADR-0135).`,
        title: `Ask the ${code} manufacturers for prices in writing`,
        body: [`Write to each shortlisted maker for a price list, minimum order, lead time and a trial batch. Record each answer from the maker's own email.`, names, from].join('\n\n'),
        to: { path: '/suppliers', label: 'Open Suppliers' },
      },
    ];
  }
  return [];
}

export const USE_GROUPS: Record<WireResearchUse['kind'], { heading: string; takes: string; verb: string }> = {
  prospect: { heading: 'Businesses that could buy', takes: 'Taken, they go to one shortlist that the market’s running plan finds on Google Maps; each found becomes a prospect.', verb: 'as a prospect' },
  competitor: { heading: 'Selling against us', takes: 'Taken, each becomes a competitor on the market’s page, with the words research said about it.', verb: 'as a competitor' },
  supplier: { heading: 'Makers', takes: 'Taken, each becomes a supplier candidate resting on the page the finding cites.', verb: 'as a supplier candidate' },
  price: { heading: 'Prices', takes: 'Taken, each goes on the market’s page as said, not confirmed, beside the seller it was seen at.', verb: 'to the market’s prices' },
  check: { heading: 'Findings to read back', takes: 'Taken, the department’s agent reads each page and keeps the finding as sourced only where the page holds it.', verb: 'to be read back' },
};

/** The task a use becomes when a person gives it out instead of taking it. */
export function useTask(u: WireResearchUse, requestId: string): { title: string; body: string } {
  const d = u.detail;
  const page = d.url ? `Page: ${d.url}` : '';
  const said = d.quote ? `The research says: “${d.quote}” (${d.criterion}).` : `${d.criterion}: ${d.finding_text ?? ''}`;
  const from = `From the research on /requests/${requestId}.`;
  switch (u.kind) {
    case 'prospect':
      return { title: `Add ${d.name} as a prospect in ${u.marketCode}`.slice(0, 200), body: [said, page, `Check it is a business that could buy from us, find its own site, and add it under the ${u.marketCode} plan.`, from].filter(Boolean).join('\n\n') };
    case 'competitor':
      return { title: `Add ${d.name} as a competitor in ${u.marketCode}`.slice(0, 200), body: [said, page, 'Record what it sells, where and at what price, with the page for each.', from].filter(Boolean).join('\n\n') };
    case 'supplier':
      return { title: `Look at ${d.name} as a maker`.slice(0, 200), body: [said, page, 'Find its own site and add it on Suppliers if it makes what we need.', from].filter(Boolean).join('\n\n') };
    case 'price':
      return {
        title: `Record ${d.name}’s price in ${u.marketCode}`.slice(0, 200),
        body: [said, page, d.price ? `${d.price.product}: ${d.price.amount} ${d.price.currency} per ${d.price.unit}.` : '', 'Check the price on the page and record it on the market’s page.', from].filter(Boolean).join('\n\n'),
      };
    case 'check':
      return { title: `Read back the ${u.marketCode} finding on ${d.criterion}`.slice(0, 200), body: [said, `Pages: ${(d.sources ?? (d.url ? [d.url] : [])).join(' ')}`, 'Open each page and check it says this; record it as sourced on the market’s page if it does.', from].join('\n\n') };
  }
}

/** One task for a whole group: "Add the 4 prospects the SE research names". */
export function groupTask(kind: WireResearchUse['kind'], uses: readonly WireResearchUse[], requestId: string): { title: string; body: string } {
  const code = uses[0]?.marketCode ?? '';
  const n = uses.length;
  const lines = uses.map((u) => `- ${u.detail.name}${u.detail.quote ? `: “${u.detail.quote}”` : ''}${u.detail.url ? ` (${u.detail.url})` : ''}`);
  const title = {
    prospect: `Add the ${n} prospect${n === 1 ? '' : 's'} the ${code} research names`,
    competitor: `Add the ${n} competitor${n === 1 ? '' : 's'} the ${code} research names`,
    supplier: `Look at the ${n} maker${n === 1 ? '' : 's'} the ${code} research names`,
    price: `Record the ${n} price${n === 1 ? '' : 's'} the ${code} research found`,
    check: `Read back ${n} ${code} finding${n === 1 ? '' : 's'}`,
  }[kind];
  return { title, body: [USE_GROUPS[kind].takes, ...lines, `From the research on /requests/${requestId}.`].join('\n\n') };
}

/** Where a taken use's record is, by what taking it made. */
export function outcomeLink(u: Pick<WireResearchUse, 'outcome' | 'marketCode'>): { to: string; label: string } | null {
  const [kind, id] = (u.outcome ?? '').split(':');
  if (!kind || !id) return null;
  if (kind === 'competitor') return { to: `/markets/${u.marketCode}`, label: `On ${u.marketCode}’s page` };
  if (kind === 'supplier') return { to: `/suppliers/${id}`, label: 'On Suppliers' };
  if (kind === 'shortlist') return { to: `/prospecting/shortlists/${id}`, label: 'On its shortlist' };
  if (kind === 'evidence_proposal') return { to: `/markets/${u.marketCode}`, label: 'Being read back' };
  return null;
}

const STATE_WORDS: Record<WireResearchUse['state'], string> = { proposed: 'Waiting for the lead', taken: 'Taken', declined: 'Declined', failed: 'Could not be taken' };

/** What a use says about itself (ADR-0155): what it is, why it is here, who found it and what to do. */
export function explainUse(u: WireResearchUse, opts: { mayTake: boolean; requestId: string }): ItemContext {
  const g = USE_GROUPS[u.kind];
  const d = u.detail;
  const link = outcomeLink(u);
  return {
    subject: { kind: 'use of research', title: d.name },
    summary: u.kind === 'check' ? `The finding on ${d.criterion}, from pages nobody has read back yet.` : `${d.name}${d.role ? `, ${d.role},` : ''} named in the research on ${d.criterion}.`,
    need:
      u.state === 'proposed'
        ? opts.mayTake
          ? `Take it ${g.verb}, or decline it.`
          : `Your lead takes it ${g.verb}, or you give it to someone as a task.`
        : u.state === 'failed'
          ? (u.note ?? 'It could not be taken.')
          : undefined,
    why: g.takes,
    source: { label: d.url ? `The research's page: ${d.url}` : 'The research answer', to: `/requests/${opts.requestId}`, when: d.researchedOn },
    broughtBy: u.foundBy ? { name: u.foundBy, kind: 'agent', role: 'read the answer and proposed it', when: u.createdAt } : null,
    facts: [
      ['Rests on', d.quote ? `“${d.quote}”` : (d.finding_text ?? null)],
      ['State', STATE_WORDS[u.state]],
      ['Decided by', u.decidedBy],
      ['Price', d.price ? `${d.price.amount} ${d.price.currency} per ${d.price.unit} (${d.price.product})` : null],
    ],
    next:
      u.state === 'proposed'
        ? { tip: opts.mayTake ? `Tick it and press Take. ${g.takes}` : 'Give it to someone as a task, or ask your lead to take it.' }
        : link
          ? { tip: `${link.label}.` }
          : { tip: 'Nothing is needed.' },
    ...(link ? { open: { to: link.to, label: link.label } } : {}),
  };
}
